/**
 * apiClient — Axios instance with tenant-aware interceptors.
 *
 * Tenant resolution (ADR-101):
 *   Every outgoing request carries `X-Tenant-Id` and `X-Branch-Id` headers.
 *   Backend re-resolves these against the authenticated user's memberships
 *   and enforces RLS at the database layer. Client headers are UX hints only.
 *
 * Auth (JWT):
 *   Token is stored in sessionStorage (cleared on tab close) to limit
 *   persistence on shared tablets. `useAuth` is the single writer.
 */
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { APP_ENV } from '../config/env';

const AUTH_TOKEN_KEY = 'petota-business:auth-token';
const ACTIVE_BRANCH_KEY = 'petota-business:active-branch';

export function getStoredAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage.getItem(AUTH_TOKEN_KEY);
}

export function setStoredAuthToken(token: string | null): void {
  if (typeof window === 'undefined') return;
  if (token) {
    window.sessionStorage.setItem(AUTH_TOKEN_KEY, token);
  } else {
    window.sessionStorage.removeItem(AUTH_TOKEN_KEY);
  }
}

export function getActiveBranchId(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(ACTIVE_BRANCH_KEY);
}

/**
 * Persists the active branch ID and is the SOLE writer for
 * `petota-business:active-branch`. `bizClient` and `TenantProvider`
 * both read it via `getActiveBranchId()`. Pass `null` to clear.
 *
 * Note: localStorage (not sessionStorage) is intentional — operators
 * expect to land on the same branch they left after closing the tab.
 */
export function setActiveBranchId(branchId: string | null): void {
  if (typeof window === 'undefined') return;
  if (branchId) {
    window.localStorage.setItem(ACTIVE_BRANCH_KEY, branchId);
  } else {
    window.localStorage.removeItem(ACTIVE_BRANCH_KEY);
  }
}

export interface ApiErrorShape {
  /** RFC 7807 Problem Details — authoritative error payload from backend. */
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  /** Optional field-level violations (zod validation). */
  violations?: Array<{ field: string; message: string; code?: string }>;
  /** Correlation ID for server-side log lookup. */
  correlationId?: string;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly payload: ApiErrorShape;
  public readonly correlationId?: string;

  constructor(payload: ApiErrorShape, httpStatus: number) {
    super(payload.detail || payload.title || `HTTP ${httpStatus}`);
    this.name = 'ApiError';
    this.status = httpStatus;
    this.payload = payload;
    if (payload.correlationId !== undefined) {
      this.correlationId = payload.correlationId;
    }
  }
}

function buildClient(): AxiosInstance {
  const instance = axios.create({
    baseURL: APP_ENV.apiBaseUrl,
    timeout: 30_000,
    withCredentials: false,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = getStoredAuthToken();
    if (token && config.headers) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    const branchId = getActiveBranchId();
    if (branchId && config.headers) {
      config.headers.set('X-Branch-Id', branchId);
    }
    return config;
  });

  instance.interceptors.response.use(
    (res) => res,
    (err: AxiosError<ApiErrorShape>) => {
      if (err.response) {
        const payload: ApiErrorShape = err.response.data ?? {
          type: 'about:blank',
          title: err.response.statusText || 'Request failed',
          status: err.response.status,
        };
        return Promise.reject(new ApiError(payload, err.response.status));
      }
      if (err.code === 'ECONNABORTED') {
        return Promise.reject(
          new ApiError(
            {
              type: 'petota:network/timeout',
              title: 'Request timed out',
              status: 0,
            },
            0,
          ),
        );
      }
      return Promise.reject(
        new ApiError(
          {
            type: 'petota:network/offline',
            title: err.message || 'Network error',
            status: 0,
          },
          0,
        ),
      );
    },
  );

  return instance;
}

export const apiClient = buildClient();

export async function apiGet<T>(
  path: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await apiClient.get<T>(path, config);
  return res.data;
}

export async function apiPost<T, B = unknown>(
  path: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await apiClient.post<T>(path, body, config);
  return res.data;
}

export async function apiPatch<T, B = unknown>(
  path: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await apiClient.patch<T>(path, body, config);
  return res.data;
}

export async function apiDelete<T>(
  path: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await apiClient.delete<T>(path, config);
  return res.data;
}
